import React, { useState, useMemo, useCallback, useLayoutEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDuplicateStory } from "../hooks/useDuplicateStory";
import { DUPLICATE_STORY_COPY } from "../hooks/useDuplicateStory.copy";
import type { MenuItemConfig } from "./registry/configRegistry.types";
import { useLocation, useNavigate, Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { breakpointValues } from "@magicbi/ui-library";
import { phoneMedia } from "../styles/mobileLayout";
import { useViewportClass } from "../hooks/useViewportClass";
import { Menu, MessageCircle, Search, Plus, UserPlus, Send, RefreshCw, Copy, Grid, Code, Download } from "react-feather";
import { Button as DsButton } from "../design-system/components/primitives/Button/Button";
import { WorkspaceSelectorDropdown } from "./organisms/WorkspaceSelectorDropdown";
import { MOCK_WORKSPACES } from "../services/workspaces/workspacesMock";
import { KgNavBranding } from "./atoms/KgNavBranding";
import ShareMenu from "./molecules/ShareMenu";
import { DeliverStoryFlow } from "./organisms/DeliverStory";
import { StoryScheduleRefreshModal } from "./organisms/StoryScheduleRefreshModal";
import type { StoryRefreshConfig } from "./organisms/StoryScheduleRefreshModal";
import {
  createStoryRefreshSchedule,
  getStoryRefreshSchedule,
  replaceStoryRefreshSchedule,
} from "../services/storySchedule/storyScheduleApi";
import { toStoryScheduleWriteRequest } from "../services/storySchedule/storyScheduleMappers";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import { useStoryAutoRefresh } from "../hooks/useStoryAutoRefresh";
import { useStoryRefreshNow } from "../hooks/useStoryRefreshNow";
import { useStoryCollaborators } from "../hooks/useStoryCollaborators";
import { useStoryOwner } from "../hooks/useStoryOwner";
import ShareStoryModal from "./organisms/ShareStoryModal";
import { StoryEmbedCodeModal } from "./organisms/StoryEmbedCodeModal";
import { StoryModeSwitch } from "./organisms/StoryModeSwitch/StoryModeSwitch";
import { StoryHeaderTitle } from "./molecules/StoryHeaderTitle";
import type { StorySqlChart } from "./organisms/StorySqlDialog";
import { useStoryViewSqlAction } from "../hooks/useStoryViewSqlAction";
import { useUpdateStoryTitle } from "../hooks/useUpdateStoryTitle";
import { ViewModeToggle } from "./molecules/ViewModeToggle";
import {
  SearchField,
  SearchIcon,
  SearchInputEl,
} from "./molecules/MarketplaceToolbar/MarketplaceToolbar.styles";
import { useGlobalPath } from "../hooks/useGlobalPath";
import { resolveLayout, resolvePrimaryNavigationMode, resolveAvatarPlacement } from "./layout";
import { useStoryWorkspace } from "../context/StoryWorkspaceContext";
import { useKgHeaderBridge } from "../context/KgHeaderContext";
import { useStoryHeaderBridge, useStoryHeaderSaveState } from "../context/StoryHeaderContext";
import { STORY_SAVE_STATUS_LABELS } from "../context/storySaveStatus";
import { SaveStatusLabel } from "./molecules/SaveStatusLabel";
import {
  PUBLISH_STATUS_OPTIONS,
  StoryHeaderActions,
} from "./molecules/StoryHeaderActions";
import type { StoryHeaderMenuAnchor } from "./molecules/StoryHeaderActions/StoryHeaderActions.types";
import { KgHeaderActionButtons } from "./molecules/KgHeaderActionButtons";
import {
  KG_ADD_DATA_MODEL_HEADER_ACTIONS,
  resolveKgSectionFromPath,
  resolveKgSectionHeaderTitle,
} from "../services/kg/kgSectionRegistry";
// import { apiService } from "../services/api";
import Toast from "../components/Toast";
import SearchBar from "./SearchBar";
import Notifications from "./Notifications";
import Stepper from "./Stepper";
import { useStepperContext } from "../contexts/StepperContext";
import { rawColors } from "../design-system/tokens/colors";
import { mediaQueries } from "../design-system/tokens/breakpoints";
import { controlSize } from "../design-system/tokens/controlSizes";
import { spacing } from "../design-system/tokens/spacing";
import { touchTargetMin } from "../styles/mobileLayout";
import { typographyMixin } from "../design-system";
import { HelpMenuHost } from "./organisms/HelpMenuHost";
import { DELIVER_COPY } from "./organisms/DeliverStory/deliverStory.copy";
import { DISTRIBUTION_SHARE_ENTRY } from "../constants/distributionHelpContent";
import { distributionPaths } from "../pages/PersonalizedDistribution/distributionPaths";
import { useStoryHeaderCommands } from "../hooks/useStoryHeaderCommands";
import { toDistributionShareNoteSegments, useDistributionShareNoteState } from "../hooks/useDistributionShareNote";
import { useDistributions } from "../hooks/useDistributions";
import { countScheduled } from "../services/delivery/deliverStoryDistribution";
import { useCommandRegistry } from "../contexts/CommandRegistryContext";
import { AccountMenuChip } from "./organisms/AccountMenuChip";
import { ThemeModeToggle } from "./organisms/ThemeModeToggle";
import { STORY_EMBED_COPY, STORY_SHARE_MENU_COPY, STORY_TOP_BAR_COPY } from "./storyHeader.copy";
import { HEADER_HEIGHT, SHARE_ICON_SIZE } from "./Header.constants";
import type { StoryHeaderNavOption } from "./molecules/StoryHeaderActions/StoryHeaderActions.types";

const HeaderContainer = styled.header<{ $twoRow?: boolean; $keepLeft?: boolean }>`
  display: grid;
  /* Top-bar spec: 52px, stroke[200] bottom edge, no shadow, the centre column up to 520px. */
  /* The centre keeps its content's width and the right icons keep theirs, so the left title truncates first and nothing overlaps.
     A short page name ("Home") keeps its width instead and the search gives way, or it is cut to one letter at 1024. */
  grid-template-columns: ${({ $keepLeft }) => ($keepLeft ? "minmax(min-content, 1fr)" : "minmax(0, 1fr)")} minmax(min-content, 32.5rem) minmax(max-content, 1fr);
  align-items: center;
  height: ${HEADER_HEIGHT};
  box-sizing: border-box;
  background-color: ${rawColors.structural.white};
  padding: 0 ${spacing[3]};
  border-bottom: 1px solid ${rawColors.stroke[200]};
  position: sticky;
  top: 0;
  z-index: 1000;
  // border: 1px solid blue;

  /* Phone: the centre column shrinks to the search icon, the sides take what they need. */
  ${mediaQueries.down.xs} {
    grid-template-columns: auto minmax(0, 1fr) auto;
    padding: 0 ${spacing[2]};
  }

  /* Story pages below desktop: two rows (crumb, title, status; then the mode switch and the actions), nothing removed.
     The mode switch is a sibling of the title in the grid, so on a narrow bar they used to share one cell and overprint. */
  ${({ $twoRow }) =>
    $twoRow &&
    css`
      /* Row 2: the mode switch keeps its natural width; the actions take the rest and scroll sideways when they
         do not fit. They used to overflow leftwards under the switch and overprint it. */
      grid-template-columns: auto minmax(0, 1fr);
      grid-template-rows: auto auto;
      height: auto;
      row-gap: ${spacing[1]};
      padding: ${spacing[1]} ${spacing[2]};
      > :nth-child(1) {
        grid-column: 1 / -1;
        grid-row: 1;
      }
      > :nth-child(2) {
        grid-column: 1;
        grid-row: 2;
        justify-content: flex-start;
      }
      > :nth-child(3) {
        grid-column: 2;
        grid-row: 2;
        min-width: 0;
        gap: ${spacing[1]};
        justify-content: flex-start;
        /* Right-aligned while it fits; when it overflows it starts at the left so every action can be scrolled to. */
        > * {
          flex-shrink: 0;
        }
        > :first-child {
          margin-inline-start: auto;
        }
        &[data-scrolled-start] {
          mask-image: linear-gradient(90deg, transparent ${spacing[2]}, #000 ${spacing[10]});
        }
        overflow-x: auto;
        overscroll-behavior-x: contain;
        scrollbar-width: none;
        &::-webkit-scrollbar {
          display: none;
        }
      }
      /* Tablet: the actions are wider than the room beside the mode switch, so they wrap onto a second line
         (right-aligned) instead of scrolling or pushing the page sideways; every action stays in view. */
      @media (min-width: ${breakpointValues.md}px) and (max-width: ${breakpointValues.lg - 1}px) {
        > :nth-child(3) {
          flex-wrap: wrap;
          justify-content: flex-end;
          row-gap: ${spacing[1]};
          overflow-x: visible;
          mask-image: none;
        }
      }
      ${phoneMedia} {
        row-gap: 0;
      }
    `}
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  /* Lets the story title shrink (ellipsis) before it runs under the centre mode pill. */
  min-width: 0;
  // border: 1px solid green;
`;

const CenterSection = styled.div`
  display: flex;
  justify-content: center;
  min-width: 0;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing[3]};
  // border: 1px solid red;

  ${mediaQueries.down.xs} {
    gap: ${spacing[1]};
  }
`;

const IconButton = styled.button<{ $open?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  /* FA-16: 32px hamburger; primary 100 bg while open. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[8]};
  height: ${spacing[8]};
  padding: 0;
  margin: 0 ${spacing[1]};
  background: ${({ $open }) => ($open ? rawColors.primary[100] : "none")};
  border-radius: 50%;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${rawColors.primary[100]};
  }

  /* Phone: 44px touch target. */
  ${mediaQueries.down.sm} {
    width: ${touchTargetMin};
    height: ${touchTargetMin};
  }
`;

/** Top-bar search button (story pages): opens the command palette. */
const PaletteSearchButton: React.FC = () => {
  const { ui } = useCommandRegistry();
  return (
    <IconButton type="button" aria-label={STORY_TOP_BAR_COPY.search} onClick={() => ui.setPaletteOpen(true)}>
      <Search size={18} />
    </IconButton>
  );
};

const VerticalDivider = styled.div`
  /* FA-16: 1x20 divider. */
  width: 1px;
  height: ${spacing[5]};
  background-color: ${rawColors.stroke[200]};
  margin: 0 ${spacing[3]};
`;

const Logo = styled.img`
  /* FA-16: 26px logo. */
  height: 1.625rem;
  margin-right: ${spacing[2]};
`;

const LogoHomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  border-radius: ${spacing[1]};

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;

const BrandText = styled.span`
  font-size: 20px;
  font-weight: bold;
  margin-right: 10px;
`;

const BrandTextMagicBI = styled.span`
  font-size: 20px;
  font-weight: bold;
  margin-right: 10px;
  color: ${rawColors.brand.primary};

  /* Phone: the logo mark stands for the brand. */
  ${mediaQueries.down.xs} {
    display: none;
  }
`;

const GlobalPageTitle = styled.span`
  /* FA-16: page name 14/600 in a 160px slot. */
  ${typographyMixin("body", 2)}
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-block;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SquareBox = styled.div`
  width: 30px;
  height: 30px;
  background-color: #b889ce;
  color: ${rawColors.text.white};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-weight: bold;
  margin-right: 10px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PrimaryText = styled.span`
  font-size: 14px;
  font-weight: bold;
`;

const SecondaryText = styled.span`
  font-size: 12px;
  color: ${rawColors.text.mediumEmphasis};
`;

const StepperWrapper = styled.div<{ $show: boolean }>`
  width: 100%;
  transition: all 0.3s ease;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  max-height: ${(props) => (props.$show ? "80px" : "0")};
  overflow: hidden;
  position: absolute;
  z-index: 1000;
  bottom: -10px;
`;

interface HeaderProps {
  /** True while the nav drawer / sidebar the hamburger controls is open. */
  menuOpen?: boolean;
  /** Drawer navigation only: the menu is docked (the button then unpins). */
  menuPinned?: boolean;
  /** Drawer navigation only: pointer on the menu button drives the hover preview. */
  onMenuPointerEnter?: (event: React.PointerEvent) => void;
  onMenuPointerLeave?: () => void;
  toggleSidebar: () => void;
  toggleChat: () => void;
  setTab: (tab: string) => void;
  onLogout: () => void;
  /** Static route title supplied by the shared layout registry. */
  pageTitle?: string;
}

const MemoizedNotifications = React.memo(() => <Notifications />);
const MemoizedSearchBar = React.memo(SearchBar);
/** Menu id of the story ⋯ menu's Duplicate row. */
const STORY_DUPLICATE_MENU_ID = "duplicate-story";
/** ⋯ menu ids for the Share rows it carries on phones: the prefix plus the Share menu value. */
const SHARE_MENU_ID_PREFIX = "share:";
const SHARE_MENU_ICONS: Record<string, MenuItemConfig["icon"]> = {
  [STORY_SHARE_MENU_COPY.collaborate.value]: UserPlus,
  [STORY_SHARE_MENU_COPY.copyEmbed.value]: Code,
  [STORY_SHARE_MENU_COPY.deliver.value]: Send,
  [STORY_SHARE_MENU_COPY.export.value]: Download,
  [DISTRIBUTION_SHARE_ENTRY.value]: Grid,
  [STORY_SHARE_MENU_COPY.activeDeliveries.value]: RefreshCw,
};
const EMPTY_SQL_CHARTS: StorySqlChart[] = [];

const KG_SEARCH_WIDTH = "min(280px, 36vw)";
const GLOBAL_PAGE_SEARCH_WIDTH = "min(602px, 48vw)";

const Header = React.memo<HeaderProps>(
  ({ toggleSidebar, toggleChat, onLogout, pageTitle, menuOpen, menuPinned, onMenuPointerEnter, onMenuPointerLeave }) => {
    const { story, workspace, workspaces, setWorkspace } = useStoryWorkspace();
    const navigate = useNavigate();
    const location = useLocation();

    const urlStoryId = useMemo(() => {
      const m = location.pathname.match(/\/story\/([0-9a-f-]{36})/i);
      return m?.[1] ?? undefined;
    }, [location.pathname]);
    const urlWorkspaceId = useMemo(() => {
      const m = location.pathname.match(/\/workspace\/([0-9a-f-]{36})/i);
      return m?.[1] ?? undefined;
    }, [location.pathname]);
    const shareOwner = useStoryOwner(urlWorkspaceId, urlStoryId);
    const {
      isGlobalPath,
      isStoryPaths,
      isLoginPath,
      isConnectorMarketplacePath,
      isKnowledgeGraphPath,
      isWorkspacesPath,
      isAgentsPath,
      isStoryDetailPath,
      isConnectorDetailPath,
    } = useGlobalPath();
    // One avatar per screen: the top bar shows it unless the persistent sidebar does.
    const showHeaderAvatar =
      resolveAvatarPlacement({
        isLoginPath,
        navigationMode: resolvePrimaryNavigationMode(
          resolveLayout(location.pathname).chrome?.primaryNavigation,
          {
            isGlobalPath,
            isConnectorDetailPath,
            isKnowledgeGraphPath,
          },
        ),
        sidebarVisible: !!menuOpen,
      }) === "header";
    // The dropdown only reads memberships captured at tenant selection. It
    // must never re-run tenant or workspace-list APIs on route changes.
    const showWorkspaceDropdown =
      isGlobalPath && !isConnectorMarketplacePath && !isKnowledgeGraphPath;
    const dropdownWorkspaces = workspaces;
    const handleManageWorkspace = useCallback(
      () => navigate("/workspaces"),
      [navigate],
    );
    const { showStepper, steps } = useStepperContext();
    const queryClient = useQueryClient();

    const [toast, setToast] = useState<{
      message: string;
      position?: string;
      duration?: number;
    } | null>(null);

    const kgHeaderBridge = useKgHeaderBridge();
    const storyHeaderBridge = useStoryHeaderBridge();
    const storySaveState = useStoryHeaderSaveState();
    const kgSection = useMemo(
      () =>
        isKnowledgeGraphPath
          ? resolveKgSectionFromPath(location.pathname)
          : undefined,
      [isKnowledgeGraphPath, location.pathname],
    );
    const kgHeaderTitle = useMemo(() => {
      if (!kgSection) return "";
      return resolveKgSectionHeaderTitle(kgSection, {
        viewMode: kgHeaderBridge?.viewMode,
      });
    }, [kgSection, kgHeaderBridge?.viewMode]);

    // Workspaces section title — mirrors `kgHeaderTitle`. List page shows
    // "All workspaces"; detail page shows the workspace name (or fallback).
    const workspaceHeaderTitle = useMemo(() => {
      if (!isWorkspacesPath) return "";
      const detailMatch = location.pathname.match(/^\/workspaces\/([^/]+)/);
      if (!detailMatch) return "All workspaces";
      const detailId = detailMatch[1];
      const ws = MOCK_WORKSPACES.find((w) => w.id === detailId);
      return ws?.name ?? "Workspace";
    }, [isWorkspacesPath, location.pathname]);
    const kgSearchPlaceholder =
      kgHeaderBridge?.searchPlaceholder ??
      kgSection?.headerChrome?.searchPlaceholder ??
      "Search nodes";
    const showKgViewModeToggle =
      kgHeaderBridge?.showViewModeToggle ??
      kgSection?.headerChrome?.showViewModeToggle ??
      false;
    const kgHeaderMode =
      kgHeaderBridge?.headerMode ?? kgSection?.headerChrome?.mode ?? "search";
    const kgHeaderActions =
      kgSection?.headerChrome?.actions ?? KG_ADD_DATA_MODEL_HEADER_ACTIONS;
    // Pages that publish controlled header chrome: KG, Workspaces, and Agents.
    // Other global pages keep the generic SearchBar.
    const supportsKgHeaderBridge =
      isKnowledgeGraphPath || isWorkspacesPath || isAgentsPath;
    const showKgHeaderActions =
      supportsKgHeaderBridge &&
      kgHeaderBridge !== null &&
      kgHeaderMode === "actions";
    const showKgHeaderSearch =
      supportsKgHeaderBridge &&
      kgHeaderBridge !== null &&
      kgHeaderMode === "search" &&
      kgHeaderBridge.setSearch != null;

    // const handleShareStory = useCallback(() => {
    //   if (workspace?.id && story?.id) {
    //     const url = `${window.location.origin}/build/${workspace.id}/${story.id}`;
    //     navigator.clipboard.writeText(url);
    //     setToast({
    //       message: "Share link copied to clipboard!",
    //       position: "bottom-right",
    //       duration: 3000,
    //     });
    //   }
    // }, [workspace?.id, story?.id]);

    const [shareMenuOpen, setShareMenuOpen] = useState(false);
    const [deliverStoryOpen, setDeliverStoryOpen] = useState(false);
    const [deliverInitialView, setDeliverInitialView] = useState<"create" | "list">("create");
    const [shareStoryOpen, setShareStoryOpen] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [refreshDropdownOpen, setRefreshDropdownOpen] = useState(false);
    const [scheduleRefreshAnchor, setScheduleRefreshAnchor] =
      useState<StoryHeaderMenuAnchor | null>(null);
    const [scheduleRefreshOpen, setScheduleRefreshOpen] = useState(false);
    const [scheduleRefreshInitialValue, setScheduleRefreshInitialValue] =
      useState<Partial<StoryRefreshConfig> | undefined>(undefined);
    const [scheduleRefreshExists, setScheduleRefreshExists] = useState(false);
    const [scheduleRefreshLoading, setScheduleRefreshLoading] = useState(false);
    const [scheduleRefreshSaving, setScheduleRefreshSaving] = useState(false);
    const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const autoRefreshWorkspaceId = String(
      workspace?.id ?? urlWorkspaceId ?? "",
    );
    const autoRefreshStoryId = String(story?.id ?? urlStoryId ?? "");
    const {
      collaborators: storyCollaborators,
      isLoading: storyCollaboratorsLoading,
      errorMessage: storyCollaboratorsError,
    } = useStoryCollaborators(autoRefreshStoryId);
    const handleRefreshError = useCallback(
      (message: string) =>
        setToast({ message, position: "bottom-right", duration: 3000 }),
      [],
    );
    const { enabled: autoRefreshEnabled, toggle: handleAutoRefreshToggle } =
      useStoryAutoRefresh(
        autoRefreshWorkspaceId,
        autoRefreshStoryId,
        handleRefreshError,
      );

    const handleRefreshNowSuccess = useCallback(
      () => {
        setToast({
          message: "Refresh started.",
          position: "bottom-right",
          duration: 3000,
        });
        // Invalidate every cached query for this story (detail, canvas
        // manifest/widgets/datasets, chat history, presentation, ...) so
        // useStoryBuildFlow picks up the "refreshing" status and the whole
        // page reflects the regenerated content — not just story detail —
        // without needing a hard browser refresh. This prefix-match already
        // covers HubSpot's canvasWidgetHubspotData keys (they nest under the
        // same ["stories", workspaceId, storyId] root), so no separate
        // HubSpot-specific predicate is needed here.
        if (autoRefreshWorkspaceId && autoRefreshStoryId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.stories.storyRoot(
              autoRefreshWorkspaceId,
              autoRefreshStoryId,
            ),
          });
          // The story row sits outside the storyRoot prefix (see queryKeys)
          // and is the one that carries the "refreshing" status.
          queryClient.invalidateQueries({
            queryKey: queryKeys.storyMeta.detail(
              autoRefreshWorkspaceId,
              autoRefreshStoryId,
            ),
          });
        }
      },
      [queryClient, autoRefreshWorkspaceId, autoRefreshStoryId],
    );
    /**
     * Shares the workspace/story ids already resolved for auto-refresh — both
     * features hang off the same header dropdown and the same story.
     */
    const { refreshNow, isRefreshing: isStoryRefreshing } = useStoryRefreshNow(
      autoRefreshWorkspaceId,
      autoRefreshStoryId,
      handleRefreshError,
      handleRefreshNowSuccess,
    );

    const handleStatusDropdownToggle = useCallback(
      () => setStatusDropdownOpen((prev) => !prev),
      [],
    );
    const handleRefreshDropdownToggle = useCallback(
      () => setRefreshDropdownOpen((prev) => !prev),
      [],
    );
    const handleShareDropdownToggle = useCallback(
      () => setShareDropdownOpen((prev) => !prev),
      [],
    );
    const handleProfileDropdownToggle = useCallback(
      () => setProfileDropdownOpen((prev) => !prev),
      [],
    );

    const handleCollaborate = useCallback(() => {
      setShareMenuOpen(false);
      setShareDropdownOpen(false);
      setShareStoryOpen(true);
    }, []);
    const [embedOpen, setEmbedOpen] = useState(false);
    const embedStoryId = String(story?.id ?? urlStoryId ?? "");
    // One mint per open: the token is created when the dialog opens, never on render.
    // The data app tab has no embed page yet: its Copy embed code row is visible but waits.
    const isDataAppTab = new URLSearchParams(location.search).get("tab") === "dataApp";
    const handleCopyEmbed = useCallback(() => {
      if (isDataAppTab) return;
      setShareMenuOpen(false);
      setShareDropdownOpen(false);
      setEmbedOpen(true);
    }, [isDataAppTab]);
    const handleCloseEmbed = useCallback(() => setEmbedOpen(false), []);
    // Command palette, chat and voice: Share and Refresh run the header's own handlers.
    useStoryHeaderCommands({
      isStoryPage: Boolean(isStoryDetailPath),
      isShareEnabled: Boolean(storyHeaderBridge?.isShareEnabled),
      onShare: handleCollaborate,
      onCopyEmbed: handleCopyEmbed,
      isCopyEmbedSoon: isDataAppTab,
      isRefreshing: isStoryRefreshing,
      onRefresh: refreshNow,
    });

    const handleDeliverStory = useCallback(() => {
      setShareMenuOpen(false);
      setShareDropdownOpen(false);
      setDeliverInitialView("create");
      setDeliverStoryOpen(true);
    }, []);

    /** Share › Active deliveries: opens the story's deliveries list. */
    const handleActiveDeliveries = useCallback(() => {
      setShareMenuOpen(false);
      setShareDropdownOpen(false);
      setDeliverInitialView("list");
      setDeliverStoryOpen(true);
    }, []);

    /** Round 6: "2 set up for this story · 1 sending now" under the Share entry, a way to the runs. */
    const distributionShareNote = useDistributionShareNoteState(
      String(story?.id ?? urlStoryId ?? "") || undefined,
      Boolean(isStoryDetailPath),
    );
    /** Share › Personalized distribution opens the story's distributions list. */
    const handlePersonalizedDistribution = useCallback(() => {
      setShareDropdownOpen(false);
      const workspaceId = String(workspace?.id ?? urlWorkspaceId ?? "");
      const storyId = String(story?.id ?? urlStoryId ?? "");
      if (workspaceId && storyId) {
        navigate(distributionPaths.list({ workspaceId, storyId }));
      }
    }, [navigate, story?.id, urlStoryId, urlWorkspaceId, workspace?.id]);

    // Share › Export opens the page's real export; pages that publish none show it as Coming soon.
    const openPageExport = storyHeaderBridge?.onOpenExport;
    const handleExport = useCallback(() => {
      setShareMenuOpen(false);
      openPageExport?.();
    }, [openPageExport]);

    /**
     * Story header refresh menu. The two items have been rendered since the
     * header was built but were inert — nothing supplied `onRefreshSelect`.
     *
     * "Schedule refresh" opens the configuration modal. "Refresh now" (F1.2)
     * fires the one-shot POST and reports the outcome as a toast — it does not
     * open anything, and there is no polling route yet, so "Refresh started."
     * is the whole of the success feedback.
     */
    const handleRefreshSelect = useCallback(
      async (value: string) => {
        setRefreshDropdownOpen(false);
        if (value === "refresh_now") {
          await refreshNow();
          return;
        }
        if (value !== "schedule_refresh") return;

        if (scheduleRefreshLoading) return;

        const workspaceId = String(workspace?.id ?? urlWorkspaceId ?? "");
        const storyId = String(story?.id ?? urlStoryId ?? "");
        if (!workspaceId || !storyId) {
          setToast({
            message: "Unable to load the refresh schedule.",
            position: "bottom-right",
            duration: 3000,
          });
          return;
        }

        setScheduleRefreshLoading(true);
        try {
          const state = await getStoryRefreshSchedule(workspaceId, storyId);
          setScheduleRefreshExists(state.schedule !== null);
          setScheduleRefreshInitialValue(state.schedule?.config);
          setScheduleRefreshOpen(true);
        } catch (error) {
          setToast({
            message: getApiErrorMessage(
              error,
              "Unable to load the refresh schedule.",
            ),
            position: "bottom-right",
            duration: 3000,
          });
        } finally {
          setScheduleRefreshLoading(false);
        }
      },
      [
        refreshNow,
        scheduleRefreshLoading,
        story?.id,
        urlStoryId,
        urlWorkspaceId,
        workspace?.id,
      ],
    );

    const handleScheduleRefreshSubmit = useCallback(
      async (storyId: string, config: StoryRefreshConfig) => {
        const workspaceId = String(workspace?.id ?? urlWorkspaceId ?? "");
        if (!workspaceId || !storyId) {
          setToast({
            message: "Unable to save the refresh schedule.",
            position: "bottom-right",
            duration: 3000,
          });
          return;
        }

        if (scheduleRefreshSaving) return;

        setScheduleRefreshSaving(true);
        try {
          const request = toStoryScheduleWriteRequest(config);
          const state = scheduleRefreshExists
            ? await replaceStoryRefreshSchedule(workspaceId, storyId, request)
            : await createStoryRefreshSchedule(workspaceId, storyId, request);

          setScheduleRefreshExists(true);
          setScheduleRefreshInitialValue(state.schedule?.config ?? config);
          setScheduleRefreshOpen(false);
          setToast({
            message: "Refresh schedule saved.",
            position: "bottom-right",
            duration: 3000,
          });
        } catch (error) {
          setToast({
            message: getApiErrorMessage(
              error,
              "Unable to save the refresh schedule.",
            ),
            position: "bottom-right",
            duration: 3000,
          });
        } finally {
          setScheduleRefreshSaving(false);
        }
      },
      [
        scheduleRefreshExists,
        scheduleRefreshSaving,
        urlWorkspaceId,
        workspace?.id,
      ],
    );

    const storyIdForActions = String(story?.id ?? urlStoryId ?? "");
    const { saveTitle } = useUpdateStoryTitle(
      workspace?.id ?? urlWorkspaceId,
      storyIdForActions || undefined,
    );
    // View SQL lives in the story ⋯ menu and ⌘K. The charts come from the
    // page through the header bridge (empty until a page publishes them).
    const sql = useStoryViewSqlAction({
      storyId: storyIdForActions,
      storyTitle: storyHeaderBridge?.storyTitle ?? story?.name ?? "",
      sqlCharts: storyHeaderBridge?.sqlCharts ?? EMPTY_SQL_CHARTS,
      openEditorItemId: storyHeaderBridge?.openEditorItemId,
      textItems: storyHeaderBridge?.sqlTextItems,
      isNewPath: storyHeaderBridge?.sqlIsNewPath,
    });
    const duplicate = useDuplicateStory({
      onOpen: (id, workspaceId) => navigate(`/workspace/${workspaceId}/story/${id}/build`),
    });
    const duplicateWorkspaceId = String(workspace?.id ?? urlWorkspaceId ?? "");
    const duplicateItem = useMemo<MenuItemConfig>(
      () => ({
        id: STORY_DUPLICATE_MENU_ID,
        label: DUPLICATE_STORY_COPY.menu,
        icon: Copy,
        disabled:
          duplicate.isPending ||
          !duplicateWorkspaceId ||
          !storyIdForActions ||
          storyHeaderBridge?.sqlIsNewPath === false,
      }),
      [duplicate.isPending, duplicateWorkspaceId, storyIdForActions, storyHeaderBridge?.sqlIsNewPath],
    );
    const storyMoreItems = useMemo(() => [duplicateItem, sql.menuItem], [duplicateItem, sql.menuItem]);
    const handleShareSelect = useCallback(
      (value: string) => {
        if (value === STORY_SHARE_MENU_COPY.collaborate.value) handleCollaborate();
        if (value === STORY_SHARE_MENU_COPY.copyEmbed.value) handleCopyEmbed();
        if (value === STORY_SHARE_MENU_COPY.deliver.value) handleDeliverStory();
        if (value === STORY_SHARE_MENU_COPY.export.value) handleExport();
        if (value === DISTRIBUTION_SHARE_ENTRY.value) handlePersonalizedDistribution();
        if (value === STORY_SHARE_MENU_COPY.activeDeliveries.value) handleActiveDeliveries();
      },
      [handleCollaborate, handleCopyEmbed, handleDeliverStory, handleExport, handlePersonalizedDistribution, handleActiveDeliveries],
    );
    const handleMoreSelect = useCallback(
      (id: string) => {
        setMoreMenuOpen(false);
        if (id.startsWith(SHARE_MENU_ID_PREFIX)) handleShareSelect(id.slice(SHARE_MENU_ID_PREFIX.length));
        if (id === sql.menuItem.id) sql.open();
        if (id === STORY_DUPLICATE_MENU_ID) duplicate.mutate({ id: storyIdForActions, workspaceId: duplicateWorkspaceId });
      },
      [sql, duplicate, storyIdForActions, duplicateWorkspaceId, handleShareSelect],
    );

    const isBuildPage = location.pathname.endsWith("/build");
    const viewportClass = useViewportClass();
    const isBelowDesktop = viewportClass !== "desktop";
    // Two-row story bar: when the actions overflow their row they scroll sideways; start scrolled to the end so
    // ⋯ (which also carries Share on phones), notifications and the account stay in view.
    const rightSectionRef = useRef<HTMLDivElement>(null);
    const isTwoRowStoryBar = isStoryDetailPath && isBelowDesktop;
    useLayoutEffect(() => {
      const row = rightSectionRef.current;
      if (!isTwoRowStoryBar || !row) return undefined;
      // Fade the leading edge only while something is scrolled out past it.
      const markStart = () => row.toggleAttribute("data-scrolled-start", row.scrollLeft > 0);
      const toEnd = () => {
        row.scrollLeft = row.scrollWidth;
        markStart();
      };
      toEnd();
      row.addEventListener("scroll", markStart, { passive: true });
      // Actions and the avatar arrive after mount: follow the row's size and its content.
      const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(toEnd);
      resize?.observe(row);
      const mutation = typeof MutationObserver === "undefined" ? null : new MutationObserver(toEnd);
      mutation?.observe(row, { childList: true, subtree: true });
      return () => {
        row.removeEventListener("scroll", markStart);
        resize?.disconnect();
        mutation?.disconnect();
      };
    }, [isTwoRowStoryBar]);
    const distributionStoryId = String(story?.id ?? urlStoryId ?? "") || undefined;
    const { distributions: storyDistributions } = useDistributions(
      isStoryDetailPath ? distributionStoryId : undefined,
    );
    const scheduledCount = countScheduled(storyDistributions);
    const shareOptions = useMemo(() => {
      const iconProps = { size: SHARE_ICON_SIZE, "aria-hidden": true } as const;
      const copy = STORY_SHARE_MENU_COPY;
      const options: StoryHeaderNavOption[] = [
        { value: copy.collaborate.value, label: copy.collaborate.label, description: copy.collaborate.description, icon: <UserPlus {...iconProps} /> },
        {
          value: copy.copyEmbed.value,
          label: copy.copyEmbed.label,
          description: copy.copyEmbed.description,
          icon: <Code {...iconProps} />,
          ...(isDataAppTab ? { disabled: true, disabledNote: STORY_EMBED_COPY.dataAppUnavailable } : {}),
        },
        { value: copy.deliver.value, label: copy.deliver.label, description: copy.deliver.description, icon: <Send {...iconProps} /> },
        {
          value: copy.export.value,
          label: copy.export.label,
          description: copy.export.description,
          icon: <Download {...iconProps} />,
          ...(openPageExport ? {} : { disabled: true, disabledNote: copy.export.unavailable }),
        },
      ];
      // Story's share menu with the Personalized distribution row (round 6 look: tinted, "New", live note).
      options.push({
        value: DISTRIBUTION_SHARE_ENTRY.value,
        label: DISTRIBUTION_SHARE_ENTRY.label,
        description: DISTRIBUTION_SHARE_ENTRY.description,
        badge: DISTRIBUTION_SHARE_ENTRY.badge,
        emptyNote: DELIVER_COPY.shareStatusNone,
        note: distributionShareNote.note ?? undefined,
        noteSegments: toDistributionShareNoteSegments(distributionShareNote),
        iconName: "grid",
        highlight: true,
        dividerBefore: true,
      });
      options.push({
        value: copy.activeDeliveries.value,
        label: copy.activeDeliveries.label,
        description: scheduledCount
          ? copy.activeDeliveries.scheduled(scheduledCount)
          : copy.activeDeliveries.none,
        icon: <RefreshCw {...iconProps} />,
        iconTone: "neutral",
        dividerBefore: true,
      });
      return options;
    }, [distributionShareNote, scheduledCount, isDataAppTab, openPageExport]);
    // Phones: the Share pill can sit past the edge of the scrolling action row, so its rows are also in the ⋯ menu.
    const storyMenuItems = useMemo<MenuItemConfig[]>(() => {
      if (viewportClass !== "phone") return storyMoreItems;
      const shareDisabled = !(storyHeaderBridge?.isShareEnabled ?? false);
      const shareItems = shareOptions.map<MenuItemConfig>((option) => ({
        id: `${SHARE_MENU_ID_PREFIX}${option.value}`,
        label: STORY_TOP_BAR_COPY.shareInMenu(option.label),
        icon: SHARE_MENU_ICONS[option.value] ?? Send,
        disabled: shareDisabled || Boolean(option.disabled),
      }));
      return [...shareItems, ...storyMoreItems];
    }, [viewportClass, storyMoreItems, shareOptions, storyHeaderBridge?.isShareEnabled, isDataAppTab]);


    const leftSection = useMemo(
      () => (
        <LeftSection>
          <IconButton
            onClick={toggleSidebar}
            type="button"
            aria-label={menuPinned ? "Unpin menu" : "Open menu"}
            aria-expanded={menuOpen ?? false}
            aria-pressed={menuPinned}
            onPointerEnter={onMenuPointerEnter}
            onPointerLeave={onMenuPointerLeave}
            $open={menuOpen}
          >
            <Menu size={18} />
          </IconButton>
          <LogoHomeLink to="/home" aria-label="Go to home">
            <Logo src="/logo.png" alt="MagicBI" />
            <BrandTextMagicBI>MagicBI</BrandTextMagicBI>
          </LogoHomeLink>
          {isStoryDetailPath ? (
            <>
              <VerticalDivider />
              {/* "Data stories / <story>": the first crumb goes to the list, in build and present alike. */}
              <StoryHeaderTitle
                workspaceName={storyHeaderBridge?.workspaceName ?? workspace?.name ?? ""}
                storyTitle={storyHeaderBridge?.storyTitle ?? story?.name ?? "Untitled"}
                badgeInitials={storyHeaderBridge?.badgeInitials}
                onTitleSave={saveTitle}
                onHomeClick={() => navigate("/stories")}
                // The Draft/Published chip with its menu lives in the title; Build page only.
                publishStatus={isBuildPage ? storyHeaderBridge?.publishStatus : undefined}
                statusDropdownOpen={statusDropdownOpen}
                onStatusDropdownToggle={handleStatusDropdownToggle}
                statusOptions={PUBLISH_STATUS_OPTIONS}
                onStatusSelect={storyHeaderBridge?.onPublishStatusSelect}
              />
            </>
          ) : isKnowledgeGraphPath ? (
            <>
              <VerticalDivider />
              <KgNavBranding title={kgHeaderTitle} />
            </>
          ) : isWorkspacesPath ? (
            <>
              <VerticalDivider />
              <KgNavBranding
                subtitle="Workspaces"
                title={workspaceHeaderTitle}
              />
            </>
          ) : isConnectorMarketplacePath ? (
            <>
              <VerticalDivider />
              <GlobalPageTitle>Data connectors</GlobalPageTitle>
            </>
          ) : pageTitle ? (
            <>
              <VerticalDivider />
              <GlobalPageTitle>{pageTitle}</GlobalPageTitle>
            </>
          ) : (
            <>
              {/* <BrandText>Studio</BrandText> */}
              <VerticalDivider />
              {isStoryPaths && (
                <>
                  <SquareBox>
                    {workspace?.name?.charAt(0)?.toUpperCase()}
                  </SquareBox>
                  <TextContainer>
                    <SecondaryText>{workspace?.name}</SecondaryText>
                    <PrimaryText>{story?.name}</PrimaryText>
                  </TextContainer>
                </>
              )}
            </>
          )}
        </LeftSection>
      ),
      [
        toggleSidebar,
        isStoryDetailPath,
        storyHeaderBridge,
        saveTitle,
        workspace?.name,
        story?.name,
        navigate,
        isKnowledgeGraphPath,
        isWorkspacesPath,
        isConnectorMarketplacePath,
        pageTitle,
        isStoryPaths,
        workspace?.name,
        story?.name,
        kgHeaderTitle,
        workspaceHeaderTitle,
        menuOpen,
        menuPinned,
        onMenuPointerEnter,
        onMenuPointerLeave,
        isBuildPage,
        statusDropdownOpen,
        handleStatusDropdownToggle,
      ],
    );

    // Every page keeps the same utilities (help, notifications, theme, account); only the KG header owns its own.
    const showGlobalUtilities = !isKnowledgeGraphPath;

    const rightSectionContent = useMemo(() => {
      return (
        <>
          {isStoryDetailPath && (
            <>
              <PaletteSearchButton />
              <HelpMenuHost />
              <VerticalDivider />
            </>
          )}
          {isStoryDetailPath && storySaveState && (
            <SaveStatusLabel
              status={storySaveState.status}
              labels={STORY_SAVE_STATUS_LABELS}
              onRetry={storySaveState.onRetry}
            />
          )}
          {isStoryDetailPath ? (
            <StoryHeaderActions
              history={storyHeaderBridge?.history}
              showStatusAndRefresh={isBuildPage}
              refreshDropdownOpen={refreshDropdownOpen}
              onRefreshDropdownToggle={handleRefreshDropdownToggle}
              onRefreshSelect={handleRefreshSelect}
              onRefreshMenuAnchorChange={setScheduleRefreshAnchor}
              isShareEnabled={storyHeaderBridge?.isShareEnabled ?? false}
              shareDropdownOpen={shareDropdownOpen}
              onShareDropdownToggle={handleShareDropdownToggle}
              shareOptions={shareOptions}
              onShareSelect={handleShareSelect}
              profileDropdownOpen={profileDropdownOpen}
              onProfileDropdownToggle={handleProfileDropdownToggle}
              moreMenuOpen={moreMenuOpen}
              onMoreMenuToggle={() => setMoreMenuOpen((open) => !open)}
              moreItems={storyMenuItems}
              onMoreSelect={handleMoreSelect}
              collaborators={storyCollaborators}
              collaboratorsLoading={storyCollaboratorsLoading}
              collaboratorsError={storyCollaboratorsError}
              refreshOptions={[
                {
                  value: "refresh_now",
                  label: "Refresh now",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.516 3.00947 16.931 3.99122 18.74 5.74L21 8"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 3V8H16"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 12C21 14.3869 20.0518 16.6761 18.364 18.364C16.6761 20.0518 14.3869 21 12 21C9.48395 20.9905 7.06897 20.0088 5.26 18.26L3 16"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 16H3V21"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                },
                {
                  value: "schedule_refresh",
                  label: "Schedule refresh",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.516 3.00947 16.931 3.99122 18.74 5.74L21 8"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 3V8H16"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 12C21 14.3869 20.0518 16.6761 18.364 18.364C16.6761 20.0518 14.3869 21 12 21C9.48395 20.9905 7.06897 20.0088 5.26 18.26L3 16"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 16H3V21"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11.6172 8.66602V12.8327L14.9505 14.4993"
                        stroke={rawColors.text.mediumEmphasis}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                },
                {
                  value: "auto_refresh",
                  label: "Auto refresh",
                  switchValue: autoRefreshEnabled,
                  onSwitchChange: handleAutoRefreshToggle,
                },
              ]}
            />
          ) : null}
          {showKgHeaderActions && kgHeaderBridge ? (
            <KgHeaderActionButtons
              actions={kgHeaderActions}
              onSaveDraft={kgHeaderBridge.onSaveDraft}
              onSaveAndVerify={kgHeaderBridge.onSaveAndVerify}
              isSaveDraftLoading={kgHeaderBridge.isSaveDraftLoading}
              isSaveVerifyLoading={kgHeaderBridge.isSaveVerifyLoading}
            />
          ) : null}
          {showKgHeaderSearch &&
          kgHeaderBridge &&
          kgHeaderBridge.searchPlacement !== "center" ? (
            <>
              <SearchField
                $width={KG_SEARCH_WIDTH}
                $controlHeight={controlSize.field.md}
              >
                <SearchIcon aria-hidden>
                  <Search size={20} />
                </SearchIcon>
                <SearchInputEl
                  type="search"
                  value={kgHeaderBridge.search ?? ""}
                  onChange={(event) =>
                    kgHeaderBridge.setSearch?.(event.target.value)
                  }
                  placeholder={kgSearchPlaceholder}
                  aria-label={kgSearchPlaceholder}
                />
              </SearchField>
              {showKgViewModeToggle &&
              kgHeaderBridge.viewMode &&
              kgHeaderBridge.setViewMode ? (
                <ViewModeToggle
                  value={kgHeaderBridge.viewMode}
                  onChange={kgHeaderBridge.setViewMode}
                  size="md"
                />
              ) : null}
            </>
          ) : null}
          {supportsKgHeaderBridge && kgHeaderBridge?.primaryAction ? (
            <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              <DsButton
                variant="primary-dark"
                size="md"
                iconBefore={
                  kgHeaderBridge.primaryAction.icon ?? <Plus size={16} />
                }
                onClick={kgHeaderBridge.primaryAction.onClick}
                style={{
                  boxSizing: "border-box",
                  height: 40,
                  minHeight: 40,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
              >
                {kgHeaderBridge.primaryAction.label}
              </DsButton>
            </span>
          ) : null}
          {showGlobalUtilities && !isStoryDetailPath ? <HelpMenuHost /> : null}
          {showWorkspaceDropdown ? (
            <WorkspaceSelectorDropdown
              workspaces={dropdownWorkspaces}
              onManageWorkspace={handleManageWorkspace}
              onWorkspaceChange={setWorkspace}
            />
          ) : null}
          {isStoryPaths && (
            <>
              <IconButton
                onClick={toggleChat}
                type="button"
                aria-label="Open chat"
              >
                <MessageCircle size={20} />
              </IconButton>
              <ShareMenu
                open={shareMenuOpen}
                onOpenChange={setShareMenuOpen}
                onCollaborate={handleCollaborate}
                onDeliverStory={handleDeliverStory}
                onExport={handleExport}
                exportDisabled={!openPageExport}
              />
              <DeliverStoryFlow
                open={deliverStoryOpen}
                initialView={deliverInitialView}
                onOpenChange={setDeliverStoryOpen}
                storyId={String(story?.id ?? urlStoryId ?? "")}
                storyTitle={storyHeaderBridge?.storyTitle ?? story?.name}
                storySummary={storyHeaderBridge?.storySummary}
                workspaceId={String(workspace?.id ?? urlWorkspaceId ?? "")}
                onExportRequest={storyHeaderBridge?.onExportRequest}
              />
            </>
          )}
          {/* New story-detail routes (/workspace/:ws/story/:id/build|present):
              the Share dropdown lives in StoryHeaderActions, so the popup
              must be mounted here for handleDeliverStory to show it. */}
          {isStoryDetailPath && (
            <>
              <DeliverStoryFlow
                open={deliverStoryOpen}
                initialView={deliverInitialView}
                onOpenChange={setDeliverStoryOpen}
                storyId={String(story?.id ?? urlStoryId ?? "")}
                storyTitle={storyHeaderBridge?.storyTitle ?? story?.name}
                storySummary={storyHeaderBridge?.storySummary}
                workspaceId={String(workspace?.id ?? urlWorkspaceId ?? "")}
                onExportRequest={storyHeaderBridge?.onExportRequest}
              />
              {/* Opened from the refresh dropdown's "Schedule refresh" item,
                  which also lives in StoryHeaderActions. */}
              <StoryScheduleRefreshModal
                open={scheduleRefreshOpen}
                onClose={() => setScheduleRefreshOpen(false)}
                storyId={String(story?.id ?? urlStoryId ?? "")}
                initialValue={scheduleRefreshInitialValue}
                onSubmit={handleScheduleRefreshSubmit}
                isSaving={scheduleRefreshSaving}
                anchor={scheduleRefreshAnchor ?? undefined}
              />
            </>
          )}
          {showGlobalUtilities ? (
            <>
              <MemoizedNotifications />
              <ThemeModeToggle />
            </>
          ) : null}
          {showHeaderAvatar ? <AccountMenuChip compact placement="bottom-end" /> : null}
          {/* Profile dropdown (Profile / Settings / Logout) moved to the
              SidebarProfileChip at the bottom of the primary sidebar. */}
        </>
      );
    }, [
      isConnectorMarketplacePath,
      isKnowledgeGraphPath,
      isStoryDetailPath,
      isStoryPaths,
      toggleChat,
      shareMenuOpen,
      deliverStoryOpen,
      deliverInitialView,
      handleCollaborate,
      handleDeliverStory,
      handleExport,
      distributionShareNote,
      handlePersonalizedDistribution,
      handleActiveDeliveries,
      kgHeaderBridge,
      kgSearchPlaceholder,
      showKgViewModeToggle,
      dropdownWorkspaces,
      handleManageWorkspace,
      setWorkspace,
      moreMenuOpen,
      storyMenuItems,
      handleShareSelect,
      handleMoreSelect,
      storyHeaderBridge,
      storySaveState,
      statusDropdownOpen,
      refreshDropdownOpen,
      scheduleRefreshOpen,
      scheduleRefreshAnchor,
      scheduleRefreshInitialValue,
      scheduleRefreshSaving,
      handleStatusDropdownToggle,
      handleRefreshDropdownToggle,
      handleRefreshSelect,
      handleScheduleRefreshSubmit,
      autoRefreshEnabled,
      handleAutoRefreshToggle,
      shareDropdownOpen,
      profileDropdownOpen,
      storyCollaborators,
      storyCollaboratorsLoading,
      storyCollaboratorsError,
      handleShareDropdownToggle,
      handleProfileDropdownToggle,
      showKgHeaderActions,
      showKgHeaderSearch,
      showWorkspaceDropdown,
      showGlobalUtilities,
      showHeaderAvatar,
      shareOptions,
      isBuildPage,
      toggleSidebar,
      supportsKgHeaderBridge,
      kgHeaderActions,
      location.pathname,
      story,
      urlStoryId,
      urlWorkspaceId,
      workspace?.id,
      setToast,
    ]);

    // Read outside the memo and listed in its deps: the story page publishes the bridge after the route
    // renders, so a memo that missed it kept Present disabled on a ready story.
    const isPresentEnabled = storyHeaderBridge?.isShareEnabled ?? false;
    const isDistributionPage = location.pathname.includes("/distributions");
    const isModeSwitchLocked =
      location.pathname.includes("/plan/") || Boolean(storyHeaderBridge?.isGenerating);

    const centerSectionContent = useMemo(
      () => (
        <>
          {showKgHeaderSearch &&
          kgHeaderBridge?.searchPlacement === "center" ? (
            <SearchField $width={GLOBAL_PAGE_SEARCH_WIDTH}>
              <SearchIcon aria-hidden>
                <Search size={20} />
              </SearchIcon>
              <SearchInputEl
                type="search"
                value={kgHeaderBridge.search ?? ""}
                onChange={(event) =>
                  kgHeaderBridge.setSearch?.(event.target.value)
                }
                placeholder={kgSearchPlaceholder}
                aria-label={kgSearchPlaceholder}
              />
            </SearchField>
          ) : null}
          {isGlobalPath &&
            !isConnectorMarketplacePath &&
            !isWorkspacesPath &&
            !isAgentsPath && <MemoizedSearchBar />}
          {isStoryDetailPath &&
            (isDistributionPage ? (
              // No mode applies off-canvas: keep the switch visible but inert, reason on hover.
              <span
                title={STORY_TOP_BAR_COPY.modeSwitchOffCanvas}
                aria-disabled="true"
                style={{ display: "inline-flex", opacity: 0.5, cursor: "not-allowed" }}
                {...{ inert: "" }}
              >
                <StoryModeSwitch isPresentEnabled={false} locked />
              </span>
            ) : (
              <StoryModeSwitch
                isPresentEnabled={isPresentEnabled}
                locked={isModeSwitchLocked}
              />
            ))}
        </>
      ),
      [
        isGlobalPath,
        isStoryPaths,
        isStoryDetailPath,
        isConnectorMarketplacePath,
        isWorkspacesPath,
        isAgentsPath,
        showKgHeaderSearch,
        kgHeaderBridge,
        kgSearchPlaceholder,
        isPresentEnabled,
        isModeSwitchLocked,
        isDistributionPage,
      ],
    );

    return useMemo(() => {
      if (isLoginPath) return null;

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <HeaderContainer $twoRow={isTwoRowStoryBar} $keepLeft={!isStoryDetailPath && (isConnectorMarketplacePath || !!pageTitle)}>
            {leftSection}
            <CenterSection>{centerSectionContent}</CenterSection>
            <RightSection ref={rightSectionRef}>{rightSectionContent}</RightSection>
            {toast && (
              <Toast
                {...toast}
                position={"bottom-right"}
                onClose={() => setToast(null)}
              />
            )}
            {/* Owned by the story page's publish mutation, so its lifetime is
                the mutation's error state rather than local toast state. */}
            {storyHeaderBridge?.publishStatusErrorMessage && (
              <Toast
                message={storyHeaderBridge.publishStatusErrorMessage}
                position="bottom-right"
                duration={3000}
                onClose={storyHeaderBridge.onPublishStatusErrorDismiss}
              />
            )}
          </HeaderContainer>
          {isStoryDetailPath && sql.dialog}
          {(isStoryPaths || isStoryDetailPath) && (
            <ShareStoryModal
              open={shareStoryOpen}
              storyId={String(story?.id ?? urlStoryId ?? "")}
              workspaceId={String(workspace?.id ?? urlWorkspaceId ?? "")}
              storyTitle={storyHeaderBridge?.storyTitle ?? story?.name}
              owner={shareOwner}
              onClose={() => setShareStoryOpen(false)}
            />
          )}
          {isStoryDetailPath && (
            <StoryEmbedCodeModal open={embedOpen} onClose={handleCloseEmbed} storyId={embedStoryId} />
          )}
          <StepperWrapper $show={showStepper}>
            <Stepper steps={steps} />
          </StepperWrapper>
        </div>
      );
    }, [
      isTwoRowStoryBar,
      shareOwner,
      isLoginPath,
      isStoryPaths,
      isStoryDetailPath,
      isConnectorMarketplacePath,
      pageTitle,
      leftSection,
      centerSectionContent,
      rightSectionContent,
      toast,
      showStepper,
      steps,
      shareStoryOpen,
      embedOpen,
      handleCloseEmbed,
      embedStoryId,
      sql.dialog,
      story?.id,
      workspace?.id,
      urlWorkspaceId,
      story?.name,
      storyHeaderBridge?.storyTitle,
      storyHeaderBridge?.publishStatusErrorMessage,
      storyHeaderBridge?.onPublishStatusErrorDismiss,
      urlStoryId,
    ]);
  },
  (prevProps, nextProps) => {
    return (
      prevProps.toggleSidebar === nextProps.toggleSidebar &&
      prevProps.menuOpen === nextProps.menuOpen &&
      prevProps.menuPinned === nextProps.menuPinned &&
      prevProps.onMenuPointerEnter === nextProps.onMenuPointerEnter &&
      prevProps.onMenuPointerLeave === nextProps.onMenuPointerLeave &&
      prevProps.toggleChat === nextProps.toggleChat &&
      prevProps.setTab === nextProps.setTab &&
      prevProps.onLogout === nextProps.onLogout &&
      prevProps.pageTitle === nextProps.pageTitle
    );
  },
);

Header.displayName = "Header";
export default Header;
